import { useEffect, useRef, useState } from 'react';

import { autocompletion } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { HighlightStyle, indentUnit, syntaxHighlighting } from '@codemirror/language';
import { lintGutter } from '@codemirror/lint';
import { EditorState, StateField } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { tags } from '@lezer/highlight';
import { graphql } from 'cm6-graphql';
import { GraphQLSchema, buildSchema } from 'graphql';
import styled from 'styled-components';

import { useAriaId } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

const highlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: theme.color.purple80 },
  { tag: tags.number, color: theme.color.orange100 },
  { tag: tags.string, color: theme.color.green100 },
  { tag: tags.bool, color: theme.color.indigo100 },
  { tag: tags.propertyName, color: theme.color.elementBlue100 },
]);

export type GraphQLEditorProps = {
  initialValue?: string;
  label: string;
  onChange: (value: string) => void;
  schemaURL?: string;
  error?: string;
  isDisabled?: boolean;
};

export const GraphQLEditor = ({
  initialValue,
  onChange,
  label,
  schemaURL,
  error = undefined,
  isDisabled = false,
}: GraphQLEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputId = useAriaId('input');
  const { t } = useScopedTranslate(locale);
  const [schema, setSchema] = useState<GraphQLSchema | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const fetchErrorMessage = t('failedToLoadSchema');

  useEffect(() => {
    const fetchSchema = async () => {
      if (!schemaURL) {
        setSchema(null);
        return;
      }
      try {
        const response = await fetch(schemaURL);
        if (!response.ok) {
          throw new Error('Failed to fetch schema');
        }
        const schemaSDL = await response.text();
        const fetchedSchema = buildSchema(schemaSDL);
        setSchema(fetchedSchema);
        setFetchError(null);
      } catch (error) {
        setFetchError(fetchErrorMessage);
        setSchema(null);
      }
    };

    if (schemaURL) {
      fetchSchema();
    }
  }, [schemaURL, fetchErrorMessage]);

  useEffect(() => {
    if (editorRef.current) {
      const initialGraphQL = initialValue ?? '';

      const changeHandler = StateField.define<string>({
        create: () => initialGraphQL,
        update(value, transaction) {
          if (transaction.docChanged) {
            const newValue = transaction.state.doc.toString();
            return newValue;
          }
          return value;
        },
      });

      const view = new EditorView({
        doc: initialGraphQL,
        extensions: [
          EditorState.readOnly.of(isDisabled),
          history(),
          keymap.of([...defaultKeymap, indentWithTab, ...historyKeymap]),
          schema ? graphql(schema) : [],
          autocompletion(),
          syntaxHighlighting(highlightStyle),
          indentUnit.of('  '),
          lineNumbers(),
          lintGutter(),
          changeHandler,
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              const currentValue = update.state.field(changeHandler);
              onChange?.(currentValue);
            }
          }),
        ],
        parent: editorRef.current,
      });

      return () => {
        view.destroy();
      };
    }
  }, [initialValue, schema, onChange, isDisabled]);

  return (
    <Stack space={1}>
      <FormLabel htmlFor={inputId} text={label} />
      <StyledEditor ref={editorRef} data-error={Boolean(error)} />
      {fetchError && <FormError text={fetchError} />}
      <FormError text={error} />
    </Stack>
  );
};

const StyledEditor = styled.div({
  '.cm-content': {
    caretColor: 'auto',
  },
  '.cm-editor': {
    overflow: 'auto',
    maxHeight: pxToRem(384),
    margin: 0,
    border: `${pxToRem(1)} solid ${theme.form.border.color}`,
    borderRadius: theme.space[1],
    padding: `${theme.space[2]} ${theme.space[3]}`,
    color: theme.form.value.color,
    backgroundColor: theme.form.bg.color,
    ':hover': {
      backgroundColor: theme.form.bg.colorHover,
    },
  },
  '.cm-lintPoint:after': {
    borderBottom: `${pxToRem(4)} solid ${theme.color.red200}`,
  },
  '.cm-gutters': {
    backgroundColor: 'inherit',
    color: theme.color.textHelper,
    border: 'none',
  },
  '.cm-editor.cm-focused': {
    backgroundColor: theme.form.bg.colorFocus,
    border: `${pxToRem(1)} solid ${theme.form.border.colorFocus}`,
    outline: 'none',
  },
  '.cm-editor.cm-focused .cm-gutters': {
    backgroundColor: theme.form.bg.colorFocus,
  },
  '&[data-error="true"] .cm-editor': {
    borderColor: theme.color.red100,
  },
  '.cm-tooltip': {
    border: 'none',
  },
  '.cm-tooltip-autocomplete ul li[aria-selected]': {
    backgroundColor: theme.color.blue100,
  },
  '.cm-diagnostic': {
    outline: 'none',
    border: 'none',
    borderRadius: theme.space[2],
    backgroundColor: theme.color.red100,
    color: theme.color.white100,
    padding: theme.space[4],
  },
  '.cm-gutterElement': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '.cm-lint-marker-error': {
    content: 'none',
    borderRadius: '100%',
    background: theme.color.red100,
    height: pxToRem(8),
    width: pxToRem(8),
    ':hover': {
      background: theme.color.red200,
    },
  },
});

const locale = {
  en: {
    failedToLoadSchema: 'Failed to load GraphQL schema. Autocompletion will not be available.',
  },
};
